import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import SlopeWorker from './slope-worker.ts?worker'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { demTileCache } from './dem-tile-cache.js'
import { renderSlopeTileGPU } from './slope-angle-gpu.js'
import { logger } from '../../utils/logger.js'
import { DEM_TILE_SIZE, DEM_MAX_ZOOM } from './dem-config.js'

const log = logger.create('slope-angle')
const PROTOCOL = 'slope'
const SOURCE_ID = 'slope-angle-source'
const LAYER_ID = 'slope-angle-layer'
const TILE_SIZE = DEM_TILE_SIZE
const PADDED_SIZE = TILE_SIZE + 2
const WORKER_POOL_SIZE = 4
const DEFAULT_OPACITY = 0.9

class SlopeAngleService {
  private workers: Worker[] = []
  private workerIndex = 0
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private pendingRequests = new Map<string, AbortController>()
  private nextRequestId = 0

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  private initWorkers(): void {
    if (this.workers.length > 0) {
      return
    }
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      this.workers.push(new SlopeWorker())
    }
  }

  private getWorker(): Worker {
    const worker = this.workers[this.workerIndex]
    this.workerIndex = (this.workerIndex + 1) % this.workers.length
    return worker
  }

  private terminateWorkers(): void {
    this.workers.forEach(w => w.terminate())
    this.workers = []
    this.workerIndex = 0
  }

  register(map: maplibregl.Map): void {
    this.map = map
    addProtocol(PROTOCOL, (params, abortController) => {
      return this.handleTileRequest(params, abortController)
    })
  }

  unregister(): void {
    this.disable()
    try {
      removeProtocol(PROTOCOL)
    } catch (_) {
      /* may not be registered */
    }
    this.terminateWorkers()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) {
      return
    }
    this._enabled = true

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'raster',
        tiles: [`${PROTOCOL}://{z}/{x}/{y}`],
        tileSize: TILE_SIZE,
        maxzoom: DEM_MAX_ZOOM,
        attribution: 'Elevation: Mapterhorn',
      })
    }

    if (!this.map.getLayer(LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LAYER_ID,
          type: 'raster',
          source: SOURCE_ID,
          paint: {
            'raster-opacity': this._opacity,
            'raster-fade-duration': 150,
          },
        },
        'slope-angle'
      )
    }
  }

  disable(): void {
    if (!this.map) {
      return
    }
    this._enabled = false

    for (const [, controller] of this.pendingRequests) {
      controller.abort()
    }
    this.pendingRequests.clear()

    if (this.map.getLayer(LAYER_ID)) {
      this.map.removeLayer(LAYER_ID)
    }
    if (this.map.getSource(SOURCE_ID)) {
      this.map.removeSource(SOURCE_ID)
    }

    this.terminateWorkers()
  }

  toggle(): void {
    if (this._enabled) {
      this.disable()
    } else {
      this.enable()
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) {
      return
    }
    // Re-add source/layers after a style change wiped them
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'raster',
        tiles: [`${PROTOCOL}://{z}/{x}/{y}`],
        tileSize: TILE_SIZE,
        maxzoom: DEM_MAX_ZOOM,
        attribution: 'Elevation: Mapterhorn',
      })
    }
    if (!this.map.getLayer(LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LAYER_ID,
          type: 'raster',
          source: SOURCE_ID,
          paint: {
            'raster-opacity': this._opacity,
            'raster-fade-duration': 150,
          },
        },
        'slope-angle'
      )
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(LAYER_ID)) {
      this.map.setPaintProperty(LAYER_ID, 'raster-opacity', this._opacity)
    }
  }

  private async fetchPaddedDem(
    z: number,
    x: number,
    y: number,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    const maxTile = Math.pow(2, z) - 1
    const padded = new Uint8ClampedArray(PADDED_SIZE * PADDED_SIZE * 4)

    const centerPixels = await demTileCache.getTile(z, x, y, signal)
    if (!centerPixels) throw new Error(`DEM fetch failed: ${z}/${x}/${y}`)

    // Copy center tile into rows 1..TILE_SIZE, cols 1..TILE_SIZE of the padded buffer
    for (let row = 0; row < TILE_SIZE; row++) {
      const srcStart = row * TILE_SIZE * 4
      const dstStart = ((row + 1) * PADDED_SIZE + 1) * 4
      padded.set(centerPixels.subarray(srcStart, srcStart + TILE_SIZE * 4), dstStart)
    }

    // Fetch 1px borders from neighbors
    type BorderResult = { pos: string; pixels: Uint8ClampedArray | null }
    const neighbors: Promise<BorderResult>[] = []

    const fetchNeighbor = (nx: number, ny: number, pos: string): Promise<BorderResult> => {
      if (ny < 0 || ny > maxTile) return Promise.resolve({ pos, pixels: null })
      const wrappedX = ((nx % (maxTile + 1)) + (maxTile + 1)) % (maxTile + 1)
      return demTileCache
        .getTile(z, wrappedX, ny, signal)
        .then(pixels => ({ pos, pixels }))
        .catch(() => ({ pos, pixels: null }))
    }

    neighbors.push(fetchNeighbor(x, y - 1, 'top'))
    neighbors.push(fetchNeighbor(x, y + 1, 'bottom'))
    neighbors.push(fetchNeighbor(x - 1, y, 'left'))
    neighbors.push(fetchNeighbor(x + 1, y, 'right'))
    neighbors.push(fetchNeighbor(x - 1, y - 1, 'tl'))
    neighbors.push(fetchNeighbor(x + 1, y - 1, 'tr'))
    neighbors.push(fetchNeighbor(x - 1, y + 1, 'bl'))
    neighbors.push(fetchNeighbor(x + 1, y + 1, 'br'))

    const results = await Promise.all(neighbors)

    for (const { pos, pixels } of results) {
      if (pos === 'top') {
        const src = pixels
        // Bottom row of top neighbor → padded row 0, cols 1..TILE_SIZE
        for (let col = 0; col < TILE_SIZE; col++) {
          const srcIdx = ((TILE_SIZE - 1) * TILE_SIZE + col) * 4
          const dstIdx = (0 * PADDED_SIZE + col + 1) * 4
          if (src) {
            padded[dstIdx] = src[srcIdx]
            padded[dstIdx + 1] = src[srcIdx + 1]
            padded[dstIdx + 2] = src[srcIdx + 2]
            padded[dstIdx + 3] = src[srcIdx + 3]
          } else {
            // Clamp to center top row
            const clampIdx = col * 4
            padded[dstIdx] = centerPixels[clampIdx]
            padded[dstIdx + 1] = centerPixels[clampIdx + 1]
            padded[dstIdx + 2] = centerPixels[clampIdx + 2]
            padded[dstIdx + 3] = centerPixels[clampIdx + 3]
          }
        }
      } else if (pos === 'bottom') {
        const src = pixels
        // Top row of bottom neighbor → padded row TILE_SIZE+1, cols 1..TILE_SIZE
        for (let col = 0; col < TILE_SIZE; col++) {
          const srcIdx = col * 4
          const dstIdx = ((TILE_SIZE + 1) * PADDED_SIZE + col + 1) * 4
          if (src) {
            padded[dstIdx] = src[srcIdx]
            padded[dstIdx + 1] = src[srcIdx + 1]
            padded[dstIdx + 2] = src[srcIdx + 2]
            padded[dstIdx + 3] = src[srcIdx + 3]
          } else {
            const clampIdx = ((TILE_SIZE - 1) * TILE_SIZE + col) * 4
            padded[dstIdx] = centerPixels[clampIdx]
            padded[dstIdx + 1] = centerPixels[clampIdx + 1]
            padded[dstIdx + 2] = centerPixels[clampIdx + 2]
            padded[dstIdx + 3] = centerPixels[clampIdx + 3]
          }
        }
      } else if (pos === 'left') {
        const src = pixels
        // Right column of left neighbor → padded col 0, rows 1..TILE_SIZE
        for (let row = 0; row < TILE_SIZE; row++) {
          const srcIdx = (row * TILE_SIZE + TILE_SIZE - 1) * 4
          const dstIdx = ((row + 1) * PADDED_SIZE + 0) * 4
          if (src) {
            padded[dstIdx] = src[srcIdx]
            padded[dstIdx + 1] = src[srcIdx + 1]
            padded[dstIdx + 2] = src[srcIdx + 2]
            padded[dstIdx + 3] = src[srcIdx + 3]
          } else {
            const clampIdx = (row * TILE_SIZE + 0) * 4
            padded[dstIdx] = centerPixels[clampIdx]
            padded[dstIdx + 1] = centerPixels[clampIdx + 1]
            padded[dstIdx + 2] = centerPixels[clampIdx + 2]
            padded[dstIdx + 3] = centerPixels[clampIdx + 3]
          }
        }
      } else if (pos === 'right') {
        const src = pixels
        // Left column of right neighbor → padded col TILE_SIZE+1, rows 1..TILE_SIZE
        for (let row = 0; row < TILE_SIZE; row++) {
          const srcIdx = (row * TILE_SIZE + 0) * 4
          const dstIdx = ((row + 1) * PADDED_SIZE + TILE_SIZE + 1) * 4
          if (src) {
            padded[dstIdx] = src[srcIdx]
            padded[dstIdx + 1] = src[srcIdx + 1]
            padded[dstIdx + 2] = src[srcIdx + 2]
            padded[dstIdx + 3] = src[srcIdx + 3]
          } else {
            const clampIdx = (row * TILE_SIZE + TILE_SIZE - 1) * 4
            padded[dstIdx] = centerPixels[clampIdx]
            padded[dstIdx + 1] = centerPixels[clampIdx + 1]
            padded[dstIdx + 2] = centerPixels[clampIdx + 2]
            padded[dstIdx + 3] = centerPixels[clampIdx + 3]
          }
        }
      } else if (pos === 'tl') {
        const src = pixels
        // Bottom-right pixel of TL neighbor → padded [0][0]
        const dstIdx = 0
        if (src) {
          const srcIdx = ((TILE_SIZE - 1) * TILE_SIZE + TILE_SIZE - 1) * 4
          padded[dstIdx] = src[srcIdx]
          padded[dstIdx + 1] = src[srcIdx + 1]
          padded[dstIdx + 2] = src[srcIdx + 2]
          padded[dstIdx + 3] = src[srcIdx + 3]
        } else {
          padded[dstIdx] = centerPixels[0]
          padded[dstIdx + 1] = centerPixels[1]
          padded[dstIdx + 2] = centerPixels[2]
          padded[dstIdx + 3] = centerPixels[3]
        }
      } else if (pos === 'tr') {
        const src = pixels
        // Bottom-left pixel of TR neighbor → padded [0][TILE_SIZE+1]
        const dstIdx = (TILE_SIZE + 1) * 4
        if (src) {
          const srcIdx = ((TILE_SIZE - 1) * TILE_SIZE + 0) * 4
          padded[dstIdx] = src[srcIdx]
          padded[dstIdx + 1] = src[srcIdx + 1]
          padded[dstIdx + 2] = src[srcIdx + 2]
          padded[dstIdx + 3] = src[srcIdx + 3]
        } else {
          const clampIdx = (TILE_SIZE - 1) * 4
          padded[dstIdx] = centerPixels[clampIdx]
          padded[dstIdx + 1] = centerPixels[clampIdx + 1]
          padded[dstIdx + 2] = centerPixels[clampIdx + 2]
          padded[dstIdx + 3] = centerPixels[clampIdx + 3]
        }
      } else if (pos === 'bl') {
        const src = pixels
        // Top-right pixel of BL neighbor → padded [TILE_SIZE+1][0]
        const dstIdx = ((TILE_SIZE + 1) * PADDED_SIZE + 0) * 4
        if (src) {
          const srcIdx = (TILE_SIZE - 1) * 4
          padded[dstIdx] = src[srcIdx]
          padded[dstIdx + 1] = src[srcIdx + 1]
          padded[dstIdx + 2] = src[srcIdx + 2]
          padded[dstIdx + 3] = src[srcIdx + 3]
        } else {
          const clampIdx = ((TILE_SIZE - 1) * TILE_SIZE + 0) * 4
          padded[dstIdx] = centerPixels[clampIdx]
          padded[dstIdx + 1] = centerPixels[clampIdx + 1]
          padded[dstIdx + 2] = centerPixels[clampIdx + 2]
          padded[dstIdx + 3] = centerPixels[clampIdx + 3]
        }
      } else if (pos === 'br') {
        const src = pixels
        // Top-left pixel of BR neighbor → padded [TILE_SIZE+1][TILE_SIZE+1]
        const dstIdx = ((TILE_SIZE + 1) * PADDED_SIZE + TILE_SIZE + 1) * 4
        if (src) {
          const srcIdx = 0
          padded[dstIdx] = src[srcIdx]
          padded[dstIdx + 1] = src[srcIdx + 1]
          padded[dstIdx + 2] = src[srcIdx + 2]
          padded[dstIdx + 3] = src[srcIdx + 3]
        } else {
          const clampIdx = ((TILE_SIZE - 1) * TILE_SIZE + TILE_SIZE - 1) * 4
          padded[dstIdx] = centerPixels[clampIdx]
          padded[dstIdx + 1] = centerPixels[clampIdx + 1]
          padded[dstIdx + 2] = centerPixels[clampIdx + 2]
          padded[dstIdx + 3] = centerPixels[clampIdx + 3]
        }
      }
    }

    return padded
  }

  private async handleTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ImageBitmap }> {
    const match = params.url.match(/(\d+)\/(\d+)\/(\d+)/)
    if (!match) {
      throw new Error(`Invalid slope tile URL: ${params.url}`)
    }

    const z = parseInt(match[1])
    const x = parseInt(match[2])
    const y = parseInt(match[3])
    const key = `${z}/${x}/${y}`

    this.pendingRequests.set(key, abortController)

    try {
      const tTotal = performance.now()

      const tDem = performance.now()
      const paddedDem = await this.fetchPaddedDem(z, x, y, abortController.signal)
      const demMs = performance.now() - tDem

      const tRender = performance.now()
      const gpuResult = renderSlopeTileGPU(paddedDem, PADDED_SIZE, TILE_SIZE, z, y)
      let slopePixels: Uint8ClampedArray
      if (gpuResult) {
        slopePixels = gpuResult
      } else {
        const centerDem = new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * 4)
        for (let row = 0; row < TILE_SIZE; row++) {
          const srcStart = ((row + 1) * PADDED_SIZE + 1) * 4
          const dstStart = row * TILE_SIZE * 4
          centerDem.set(paddedDem.subarray(srcStart, srcStart + TILE_SIZE * 4), dstStart)
        }
        slopePixels = await this.processInWorkerWithInit(centerDem, z, y, abortController.signal)
      }
      const renderMs = performance.now() - tRender

      // Hand the decoded RGBA straight to MapLibre as an ImageBitmap. MapLibre v5 uses a
      // protocol response whose `data` is already an ImageBitmap as-is (no image decode),
      // which avoids any encoded-image container. This matters because Chromium's BMP decoder
      // ignores the alpha channel (rendering transparent tiles as solid black), and PNG would
      // add encode cost — createImageBitmap is native, off-thread, and carries true alpha.
      // premultiplyAlpha: 'none' is required: MapLibre uploads the ImageBitmap with
      // UNPACK_PREMULTIPLY_ALPHA=true (it premultiplies during texImage2D), so the source
      // must carry STRAIGHT alpha. Leaving it at the engine default makes Chromium and
      // Firefox resolve premultiply differently for an ImageData source — one renders
      // transparent tiles as solid black. Pinning it to 'none' is correct on both engines.
      const tEncode = performance.now()
      const bitmap = await createImageBitmap(
        new ImageData(slopePixels as Uint8ClampedArray<ArrayBuffer>, TILE_SIZE, TILE_SIZE),
        { premultiplyAlpha: 'none' }
      )
      const encodeMs = performance.now() - tEncode

      const totalMs = performance.now() - tTotal
      log.debug(
        `tile ${z}/${x}/${y} total=${totalMs.toFixed(1)}ms dem=${demMs.toFixed(1)}ms render=${renderMs.toFixed(1)}ms(${gpuResult ? 'gpu' : 'cpu'}) encode=${encodeMs.toFixed(1)}ms`
      )

      return { data: bitmap }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e
      log.warn(`tile ${z}/${x}/${y} failed:`, e)
      return {
        data: await createImageBitmap(new ImageData(TILE_SIZE, TILE_SIZE), {
          premultiplyAlpha: 'none',
        }),
      }
    } finally {
      this.pendingRequests.delete(key)
    }
  }

  private processInWorkerWithInit(
    demPixels: Uint8ClampedArray,
    z: number,
    y: number,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    if (this.workers.length === 0) {
      log.info('GPU unavailable, falling back to CPU workers')
    }
    this.initWorkers()
    return this.processInWorker(demPixels, z, y, signal)
  }

  private processInWorker(
    demPixels: Uint8ClampedArray,
    z: number,
    y: number,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      const requestId = this.nextRequestId++
      const worker = this.getWorker()
      const handler = (e: MessageEvent): void => {
        if (e.data.requestId !== requestId) {
          return
        }
        worker.removeEventListener('message', handler)
        signal.removeEventListener('abort', onAbort)
        resolve(new Uint8ClampedArray(e.data.pixels))
      }
      const onAbort = (): void => {
        worker.removeEventListener('message', handler)
        reject(new DOMException('Aborted', 'AbortError'))
      }

      worker.addEventListener('message', handler)
      signal.addEventListener('abort', onAbort)
      worker.postMessage({ demPixels, z, y, requestId })
    })
  }
}

export const slopeAngleService = new SlopeAngleService()
